import type { CSSProperties, FormEvent, KeyboardEvent } from 'react';
import { useEffect, useId, useRef } from 'react';
import { cn } from 'helpers/cn';
import { AiButton } from './AiButton';
import { AiToggle } from './AiToggle';
import { GlowHalo, LogoBars } from './LogoBars';

type AiAskFieldProps = {
  /** The AI panel this field asks in. */
  open: boolean;
  onToggle: () => void;
  /** A question is being answered: the field, or the toggle, glows. */
  busy: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  /**
   * Sends the draft: the caller opens its panel with the question asked (or waiting on the notice).
   * Only called with something typed; an empty Ask opens the panel through `onToggle`.
   */
  onSubmit: () => void;
  /** The field's accessible name, read by a screen reader only. */
  label: string;
  placeholder: string;
  /** The product tour's anchor, on the field and on the toggle alike. */
  tourAnchor?: string;
};

/** Focus that went with an element as it unmounted lands on the body. */
const focusWasLost = () => !document.activeElement || document.activeElement === document.body;

/**
 * Enter that confirms an IME composition picks a word; it does not send. Safari reports that Enter
 * with `isComposing` false, and only `keyCode` 229 says what it is.
 */
export const isCompositionEnter = (ev: KeyboardEvent<HTMLElement>) =>
  ev.key === 'Enter' && (ev.nativeEvent.isComposing || ev.keyCode === 229);

/**
 * Where people ask an AI panel a question, on the page it is about (decided 2026-10-20): a field
 * while the panel is closed, there to invite the first question, standing out on the strip colour
 * with the bars and the Ink Ask button; sending opens the panel with the question, and Ask with
 * nothing typed opens it on the conversation. While the panel is open it is the Ink "Ask" toggle,
 * pressed, so there is only ever one input: the panel's. It glows while an answer is written, so a
 * question still running with the panel closed shows.
 *
 * The swap unmounts whichever was focused, so focus is handed on: to the toggle when the panel
 * opens (the panel then moves it to its own input), and back to the field when it closes. The id
 * is generated, so a page that renders the field twice (a desktop and a phone layout) never has
 * two inputs with one id.
 */
export const AiAskField = (props: AiAskFieldProps) => {
  const { open, onToggle, busy, draft, onDraftChange, onSubmit, label, placeholder, tourAnchor } = props;
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);

  // Focus management across the swap, not data fetching.
  useEffect(() => {
    if (wasOpen.current !== open && focusWasLost()) {
      (open ? toggleRef.current : inputRef.current)?.focus({ preventScroll: true });
    }
    wasOpen.current = open;
  }, [open]);

  if (open) {
    return (
      <AiToggle ref={toggleRef} pressed busy={busy} onClick={onToggle} data-tour={tourAnchor}>
        Ask
      </AiToggle>
    );
  }

  const glow = busy ? 'is-thinking' : undefined;

  // With nothing typed, Ask opens the conversation: in a shared thread, reading what colleagues
  // asked is often the first thing to do.
  const submit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (draft.trim()) onSubmit();
    else onToggle();
  };

  const guardComposition = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (isCompositionEnter(ev)) ev.preventDefault();
  };

  return (
    <form
      onSubmit={submit}
      aria-busy={busy}
      data-tour={tourAnchor}
      // The glow paints its own fill; on this box that is the strip colour, not the panel's. The
      // outline is the input's, set here because ai-glow-box's own border rule wins over a class.
      style={
        {
          '--ai-fill': 'hsl(var(--panel2))',
          borderColor: glow ? undefined : 'hsl(var(--input))',
        } as CSSProperties
      }
      // Up to 420px, and narrower when the row has less room: never at the cost of what is beside it.
      className={cn(
        'ai-glow-box flex w-full max-w-[420px] min-w-0 items-center gap-2.5 rounded-xl bg-panel2 py-1 pr-1 pl-3',
        'focus-within:ring-2 focus-within:ring-ring',
        glow && 'ai-glow',
        glow,
      )}
    >
      <GlowHalo />
      <LogoBars size={14} state={busy ? 'thinking' : 'idle'} />
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={draft}
        onChange={(ev) => onDraftChange(ev.target.value)}
        onKeyDown={guardComposition}
        placeholder={placeholder}
        className="h-9 min-w-0 flex-1 border-0 bg-transparent text-body text-foreground outline-none placeholder:text-dim"
      />
      <AiButton type="submit">Ask</AiButton>
    </form>
  );
};
